import _ from 'lodash';
import React, { createContext, FunctionComponent, useContext, useReducer } from 'react';

interface DialogsState {
    dialogs: { id: string; dialog: React.ReactNode }[];
    showLoading: boolean;
    priceFormatter: Intl.NumberFormat;
    loadedScripts: string[]; // Changed from isBabylonLoaded to loadedScripts
    user: {
        id: number;
        name: string;
    };
}

type DialogsStateReducer = (state: DialogsState, action: any) => DialogsState;
type DialogsStateDispatch = (action: any) => void;

const initialState: DialogsState = {
    dialogs: [],
    showLoading: false,
    priceFormatter: new Intl.NumberFormat(),
    loadedScripts: [], // Changed from isBabylonLoaded to loadedScripts
    user: { id: 0, name: '' }
};

const context = createContext<{
    state: DialogsState;
    dispatch: DialogsStateDispatch;
}>({
    state: initialState,
    dispatch: () => new Error('Component not wrapped in state provider.')
});

const reducer = (state: DialogsState, action: any) => {
    const newState = { ...state };
    switch (action.type) {
        case 'PRICE_FORMATTER':
            newState.priceFormatter = action.priceFormatter;
            break;
        case 'SHOW_LOADING':
            newState.dialogs = action.showLoading;
            break;
        case 'SHOW_DIALOG':
            {
                const id = (action.id || new Date().getUTCMilliseconds()).toString();

                if (!newState.dialogs.find((x) => x.id === id)) {
                    newState.dialogs = [
                        ...newState.dialogs,
                        {
                            id,
                            dialog: React.cloneElement(action.dialog, { key: id })
                        }
                    ];
                }
            }
            break;
        case 'CLOSE_DIALOG':
            newState.dialogs = newState.dialogs.filter((x) => {
                return x.id != action.dialogId;
            });
            break;
        case 'ADD_LOADED_SCRIPT':
            if (!newState.loadedScripts.includes(action.scriptUrl)) {
                newState.loadedScripts = [...newState.loadedScripts, action.scriptUrl];
            }
            break;
        default:
            throw new Error();
    }

    return newState;
};

const DialogsStateProvider: FunctionComponent<{
    children?: React.ReactNode;
}> = ({ children }) => {
    const [state, dispatch] = useReducer<DialogsStateReducer>(reducer, initialState);
    return <context.Provider value={{ state, dispatch }}>{children}</context.Provider>;
};

const useDialogsState = function () {
    return useContext(context);
};

export { DialogsStateProvider, useDialogsState };
